import type {
  CriteriaPurpose,
  CriterionTranscriptionAssignment,
  Essay,
  EssayPurpose,
  Purpose,
  Transcription,
  User,
} from '../../types';

export type TranscriptionSource = 'browse' | 'existing';

export interface AssignmentCandidate {
  transcriptionId: string;
  paperNumber: number;
  batchName: string;
  versionName: string;
  isGroundTruth: boolean;
  purposeNames: string[];
  purposeIds: string[];
  assignedUserIds: string[];
  assignedUserNames: string[];
  purposeMismatch: boolean;
}

export interface AssignmentCandidateLookups {
  transcriptions: Transcription[];
  essays: Essay[];
  essayPurposes: EssayPurpose[];
  purposes: Purpose[];
  criteriaPurposes: CriteriaPurpose[];
  assignments: CriterionTranscriptionAssignment[];
  users: User[];
}

export const CANDIDATES_PER_PAGE = 25;

export const userDisplayName = (user: User | undefined): string =>
  user ? `${user.firstName} ${user.lastName}`.trim() : 'Unknown Labeller';

export const criterionPurposeIds = (
  criteriaId: string,
  criteriaPurposes: CriteriaPurpose[]
): string[] =>
  criteriaPurposes.filter(row => row.criteriaId === criteriaId).map(row => row.purposeId);

export const transcriptionPurposeIds = (essayId: string, essayPurposes: EssayPurpose[]): string[] =>
  essayPurposes.filter(row => row.essayId === essayId).map(row => row.purposeId);

export const matchesCriteriaPurposes = (
  essayPurposeIds: string[],
  criteriaIds: string[],
  criteriaPurposes: CriteriaPurpose[]
): boolean => {
  if (criteriaIds.length === 0) return false;
  return criteriaIds.every(criteriaId => {
    const needed = criterionPurposeIds(criteriaId, criteriaPurposes);
    if (needed.length === 0) return false;
    return needed.some(purposeId => essayPurposeIds.includes(purposeId));
  });
};

export const isFullyAssigned = (
  transcriptionId: string,
  criteriaIds: string[],
  userIds: string[],
  assignments: CriterionTranscriptionAssignment[]
): boolean => {
  if (criteriaIds.length === 0 || userIds.length === 0) return true;
  return criteriaIds.every(criteriaId =>
    userIds.every(userId =>
      assignments.some(
        assignment =>
          assignment.transcriptionId === transcriptionId &&
          assignment.criteriaId === criteriaId &&
          assignment.userId === userId
      )
    )
  );
};

export const sourceTranscriptionIds = (
  assignments: CriterionTranscriptionAssignment[],
  userId: string,
  criteriaId: string
): string[] => {
  if (!userId || !criteriaId) return [];
  return Array.from(
    new Set(
      assignments
        .filter(assignment => assignment.userId === userId && assignment.criteriaId === criteriaId)
        .map(assignment => assignment.transcriptionId)
    )
  );
};

const assigneesForTranscription = (
  transcriptionId: string,
  criteriaIds: string[],
  assignments: CriterionTranscriptionAssignment[],
  usersById: Map<string, User>
): { assignedUserIds: string[]; assignedUserNames: string[] } => {
  const relevantCriteria = new Set(criteriaIds);
  const ids: string[] = [];
  assignments.forEach(assignment => {
    if (assignment.transcriptionId !== transcriptionId) return;
    if (relevantCriteria.size > 0 && !relevantCriteria.has(assignment.criteriaId)) return;
    if (!ids.includes(assignment.userId)) ids.push(assignment.userId);
  });
  return {
    assignedUserIds: ids,
    assignedUserNames: ids.map(id => userDisplayName(usersById.get(id))),
  };
};

export const buildAssignmentCandidate = (
  transcription: Transcription,
  lookups: AssignmentCandidateLookups,
  targetCriteriaIds: string[],
  assigneeCriteriaIds: string[]
): AssignmentCandidate | null => {
  const essay = lookups.essays.find(item => item.id === transcription.essayId);
  if (!essay) return null;
  const purposeIds = transcriptionPurposeIds(transcription.essayId, lookups.essayPurposes);
  const purposeNames = purposeIds
    .map(id => lookups.purposes.find(purpose => purpose.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  const usersById = new Map(lookups.users.map(user => [user.id, user]));
  const assignees = assigneesForTranscription(
    transcription.id,
    assigneeCriteriaIds,
    lookups.assignments,
    usersById
  );
  return {
    transcriptionId: transcription.id,
    paperNumber: essay.paper_number,
    batchName: essay.learn_cycle_batch,
    versionName: transcription.versionName,
    isGroundTruth: transcription.versionName === 'ground_truth',
    purposeNames,
    purposeIds,
    assignedUserIds: assignees.assignedUserIds,
    assignedUserNames: assignees.assignedUserNames,
    purposeMismatch: !matchesCriteriaPurposes(
      purposeIds,
      targetCriteriaIds,
      lookups.criteriaPurposes
    ),
  };
};

export const listAssignmentCandidates = (
  lookups: AssignmentCandidateLookups,
  options: {
    source: TranscriptionSource;
    targetCriteriaIds: string[];
    purposeIds: string[];
    preferGroundTruth: boolean;
    referenceUserId: string;
    sourceCriterionId: string;
  }
): AssignmentCandidate[] => {
  const {
    source,
    targetCriteriaIds,
    purposeIds,
    preferGroundTruth,
    referenceUserId,
    sourceCriterionId,
  } = options;

  let pool = lookups.transcriptions;
  const assigneeCriteriaIds =
    source === 'existing' && sourceCriterionId
      ? Array.from(new Set([...targetCriteriaIds, sourceCriterionId]))
      : targetCriteriaIds;

  if (source === 'existing') {
    const allowed = new Set(
      sourceTranscriptionIds(lookups.assignments, referenceUserId, sourceCriterionId)
    );
    pool = pool.filter(transcription => allowed.has(transcription.id));
  } else if (targetCriteriaIds.length === 0) {
    return [];
  }

  const candidates: AssignmentCandidate[] = [];
  pool.forEach(transcription => {
    const candidate = buildAssignmentCandidate(
      transcription,
      lookups,
      targetCriteriaIds,
      assigneeCriteriaIds
    );
    if (!candidate) return;
    if (source === 'browse' && candidate.purposeMismatch) return;
    if (purposeIds.length > 0 && !purposeIds.some(id => candidate.purposeIds.includes(id))) return;
    candidates.push(candidate);
  });

  return candidates.sort((a, b) => {
    if (preferGroundTruth && a.isGroundTruth !== b.isGroundTruth) {
      return a.isGroundTruth ? -1 : 1;
    }
    if (a.paperNumber !== b.paperNumber) return a.paperNumber - b.paperNumber;
    return a.versionName.localeCompare(b.versionName);
  });
};

export const selectableCandidateIds = (
  candidates: AssignmentCandidate[],
  criteriaIds: string[],
  userIds: string[],
  assignments: CriterionTranscriptionAssignment[]
): string[] => {
  if (userIds.length === 0 || criteriaIds.length === 0) return [];
  return candidates
    .filter(
      candidate =>
        !candidate.purposeMismatch &&
        !isFullyAssigned(candidate.transcriptionId, criteriaIds, userIds, assignments)
    )
    .map(candidate => candidate.transcriptionId);
};
